import React, { useState, useEffect } from "react";
import { Col, Spinner } from "react-bootstrap";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { ExclamationCircle, PlusLg } from "react-bootstrap-icons";
import Img from "../img/no-projects.svg";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import TemplateModal from "../../../components/modals/templateModal";
import SearchBar from "../../../components/searchBar";
import { useDomainDeleteMutation } from "../../../graphql/projects.hooks";
import DeleteProjectModal from "../../../components/modals/deleteProjectModal";
import { NetworkStatus } from "@apollo/client";
import { ProjectListGrid } from "../projectPage/ProjectListGrid";
import { ProjectListTable } from "../projectPage/ProjectListTable";
import { NotFoundPlug } from "../../../components/plugs/notFound/NotFoundPlug";
import { PremiumPlanToast } from "../../../components/inputs/toast/premiumPlanToast/PremiumPlanToast";
import { isShowNewProjectModal } from "../../../graphql/state";

type ProjectsListProps = {
  isReadonly?: boolean;
};

export default function ProjectsList({
  isReadonly = false,
}: ProjectsListProps) {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const { data, loading, refetch, networkStatus } = useUserQuery({
    notifyOnNetworkStatusChange: true,
  });
  const mePageSettings = JSON.parse(localStorage.getItem("mePageSettings"));
  const projects = data?.user?.domains || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPremuimToast, setShowPremiumToast] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<"grid" | "table">(
    mePageSettings?.project?.viewMode || "grid"
  );
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedProject, setSelectedProject] = useState(null);
  const handleCloseTemplateModal = () => {
    if (searchParams.get("isTemplates")) {
      searchParams.delete("isTemplates");

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
    setShowTemplateModal(false);
  };

  const handleDeleteProject = async () => {
    if (selectedProject) {
      const domainId = selectedProject?.domain
        ? selectedProject?.domain?.id
        : selectedProject.id;
      setIsLoading(true);
      await deleteProject({ variables: { domainId: domainId } });
    }
    handleCloseDeleteModal();
  };

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem("mePageSettings"));
    localStorage.setItem(
      "mePageSettings",
      JSON.stringify({
        ...settings,
        project: { viewMode },
      })
    );
  }, [viewMode]);

  useEffect(() => {
    if (!data?.user) return;

    if (
      !data?.user.activePremium &&
      !data?.user?.admin &&
      data?.user.domains.length === 4
    ) {
      setShowPremiumToast(true);
    } else {
      setShowPremiumToast(false);
    }
  }, [data]);

  const handleCloseDeleteModal = () => {
    setSelectedProject(null);
    setShowDeleteModal(false);
  };
  const handleShowDeleteModal = (project: any) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const filteredProjects = projects.filter((project) =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [deleteProject] = useDomainDeleteMutation({
    onCompleted: () => {
      setTimeout(() => {
        refetch();
      }, 1000);
    },
  });

  useEffect(() => {
    if (networkStatus === NetworkStatus.refetch) {
      setIsLoading(true);
    } else if (networkStatus === NetworkStatus.ready) {
      setIsLoading(false);
    }
  }, [networkStatus]);

  useEffect(() => {
    if (
      !isReadonly &&
      ((!loading && projects.length <= 0) ||
        (!loading && searchParams.get("isTemplates")))
    ) {
      setShowTemplateModal(true);
    }
  }, [isReadonly, loading, projects.length, searchParams]);

  if (loading) {
    return <Spinner animation="border" role="status"></Spinner>;
  }

  return (
    <>
      <TemplateModal
        showModal={showTemplateModal}
        handleCloseModal={handleCloseTemplateModal}
      />
      <DeleteProjectModal
        title={selectedProject?.title || ""}
        showModal={showDeleteModal}
        handleCloseModal={handleCloseDeleteModal}
        onDelete={handleDeleteProject}
      />
      <PremiumPlanToast
        show={showPremuimToast}
        onCancel={() => setShowPremiumToast(false)}
        icon={
          <ExclamationCircle className="mt-1 me-2 me-md-3 align-self-start text-warning-secondary icon-size" />
        }
        message={
          <p className="m-0 text-secondary mobile-text fs-5 col-11 col-md-auto">
            {t(
              "modal.You’ve used almost all free projects. Сlear unused pages or upgrade to"
            )}{" "}
            <span className="fw-bold">{t("payment.Premium")}</span>
          </p>
        }
        backgroundStyles="mb-4 mt-3 bg-warning-light border border-warning-secondary"
        button={
          <button
            onClick={() =>
              (window.location.href = `${process.env.REACT_APP_ME_PAGE_URL}${lng}/pricing/#pricingSection`)
            }
            className="ms-3 bg-secondary text-white fw-bold border-0 text-nowrap button-padding rounded"
          >
            {t("modal.Upgrade Now")}
          </button>
        }
        isSeenClose={true}
      />

      {projects.length > 0 ? (
        <div>
          <SearchBar
            t={t}
            title={t("projects.main")}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            viewMode={viewMode}
            setViewMode={setViewMode}
            isProjects={true}
            isDisabled={!filteredProjects}
          />
          {filteredProjects.length ? (
            viewMode === "grid" ? (
              <ProjectListGrid
                projects={filteredProjects}
                loading={isLoading || loading}
                handleShowDeleteModal={handleShowDeleteModal}
              />
            ) : (
              <ProjectListTable
                projects={filteredProjects}
                handleShowDeleteModal={handleShowDeleteModal}
              />
            )
          ) : (
            <NotFoundPlug />
          )}
        </div>
      ) : (
        <div className="bg-white px-4 py-5 text-center rounded noProjectContainer">
          <Col xl={8} xs={12} className="mx-auto">
            <img
              src={Img}
              alt={t("projects.noProject")}
              className="img-fluid mb-md-5 mb-4"
              loading="lazy"
            />
            <h2 className="mb-md-3 text-capitalize">
              {t("projects.noProject")}
            </h2>
            <p className="text-gray noProjects-text mx-auto fs-7">
              {t("projects.noProjectDescription")}
            </p>
            <div className="mx-auto">
              <button
                onClick={() => isShowNewProjectModal(true)}
                className="btn-primary btn text-white"
              >
                <PlusLg className="me-2 fs-6" />
                {t("projects.createButton")}
              </button>
            </div>
          </Col>
        </div>
      )}
    </>
  );
}
